import { useCallback, useEffect, useRef, useState } from 'react'
import { Grid, List } from 'semantic-ui-react'
import { faPlus } from '@fortawesome/pro-regular-svg-icons'
import { uniq } from 'lodash'
import './styles.scss'

import {
  Button,
  GridRowColumn,
  Icon,
  Text,
} from '@/componentsV1/BaseComponents'
import { TransactionCard } from '@/features/transactions/lists/TransactionCard'
import { TransactionHeader } from '@/features/transactions/lists/TransactionHeader'
import { AddTransactionCard } from '@/componentsV1/Transactions/AddTransactionCard'
import { TransactionCategoryIdentifier } from '@/reducers/admin/allTransactions.slice'
import { StepHeaders } from '@/features/bookkeeping/step-header'
import type { Colors } from '@/styles/theme'
import type { StepContext } from '@/features/bookkeeping/end-of-year-review/types'
import { Dialog, Button as UIButton } from '@/ui'

const filterCategories = [
  TransactionCategoryIdentifier.client_discounts_refunds,
  TransactionCategoryIdentifier.consulting_income,
  TransactionCategoryIdentifier.other_income,
  TransactionCategoryIdentifier.retail_income,
  TransactionCategoryIdentifier.speaking_income,
  TransactionCategoryIdentifier.supervision_income,
  TransactionCategoryIdentifier.teaching_income,
  TransactionCategoryIdentifier.uncategorized_income,
]

export const BusinessIncomeInPersonal = ({
  setIsStepComplete,
  stepContext,
  setStepContext,
}: {
  setIsStepComplete: (value: boolean) => void
  stepContext?: StepContext
  setStepContext?: (
    context: StepContext | ((prev: StepContext) => StepContext)
  ) => Promise<void>
}) => {
  const [showAddTransactionModal, setShowAddTransactionModal] = useState(false)
  const [addedTransactionIds, setAddedTransactionIds] = useState<number[]>([])
  const [editingTransactionId, setEditingTransactionId] = useState<number>()
  const [transcationModalColor, setTranscationModalColor] =
    useState<keyof typeof Colors>('natural')
  const scrollRef = useRef<HTMLSpanElement>(null)
  const scrollToRef = useCallback(
    () => scrollRef.current?.scrollIntoView({ behavior: 'smooth' }),
    []
  )
  const [hasBusinessIncomeToAdd, setHasBusinessIncomeToAdd] =
    useState<boolean>()
  const lastLoadedContextRef = useRef<string>('')
  const [showConfirmNoDialog, setShowConfirmNoDialog] = useState(false)

  // Load initial state from stepContext
  // Reload when stepContext changes and has meaningful data
  useEffect(() => {
    if (stepContext) {
      // Create a signature of the context to detect changes
      // Use null for undefined to ensure consistent stringification
      const contextSignature = JSON.stringify({
        hasBusinessIncomeToAdd: stepContext.hasBusinessIncomeToAdd ?? null,
        businessIncomeTransactionIds:
          stepContext.businessIncomeTransactionIds ?? null,
      })

      // Only load if the context has actually changed
      if (contextSignature !== lastLoadedContextRef.current) {
        // Load the value whether it's true, false, or undefined
        // This ensures false values are properly loaded
        setHasBusinessIncomeToAdd(stepContext.hasBusinessIncomeToAdd)
        // Always load transaction IDs if they exist in context
        if (stepContext.businessIncomeTransactionIds !== undefined) {
          setAddedTransactionIds(stepContext.businessIncomeTransactionIds)
        }
        lastLoadedContextRef.current = contextSignature
      }
    }
  }, [stepContext])

  useEffect(() => {
    if (hasBusinessIncomeToAdd !== undefined) {
      setIsStepComplete(true)
    } else {
      setIsStepComplete(false)
    }
  }, [hasBusinessIncomeToAdd, setIsStepComplete])

  useEffect(() => {
    if (showAddTransactionModal) {
      scrollToRef()
    }
  }, [scrollToRef, showAddTransactionModal])

  useEffect(() => {
    if (!hasBusinessIncomeToAdd) {
      setShowAddTransactionModal(false)
    }
  }, [hasBusinessIncomeToAdd])

  useEffect(() => {
    if (!showAddTransactionModal) {
      setEditingTransactionId(undefined)
    }
  }, [showAddTransactionModal])

  const addTransactionToList = (transactionId: number) => {
    const newIds = uniq([...addedTransactionIds, transactionId])
    setAddedTransactionIds(newIds)
    // Immediately save to context
    if (setStepContext) {
      setStepContext((prev) => ({
        ...prev,
        businessIncomeTransactionIds: newIds,
      }))
    }
  }

  const editTransaction = (transactionId: number) => {
    setShowAddTransactionModal(true)
    setEditingTransactionId(transactionId)
    setTranscationModalColor('stone')
  }

  const deleteTransaction = (transactionId: number) => {
    const newIds = addedTransactionIds.filter((tId) => tId !== transactionId)
    setAddedTransactionIds(newIds)
    // Immediately save to context
    if (setStepContext) {
      setStepContext((prev) => ({
        ...prev,
        businessIncomeTransactionIds: newIds,
      }))
    }
  }

  return (
    <Grid>
      <GridRowColumn centerContent>
        <StepHeaders
          imageSrc="https://heard-images.s3.amazonaws.com/assets/bookkeeping.svg"
          imageAlt="bookkeeping"
          imageWidth={180}
          imageHeight={180}
          title="Do you have business income from these categories in your personal bank accounts?"
          customColor="green"
        >
          <Grid>
            <Grid.Row className="list-column">
              <Grid.Column width={8}>
                <List bulleted className="flex-column">
                  <List.Item>Client Discounts/Refunds</List.Item>
                  <List.Item>Consulting Income</List.Item>
                  <List.Item>Retail Income</List.Item>
                  <List.Item>Other Income</List.Item>
                </List>
              </Grid.Column>
              <Grid.Column width={8}>
                <List bulleted className="flex-column">
                  <List.Item>Speaking Income</List.Item>
                  <List.Item>Supervision Income</List.Item>
                  <List.Item>Teaching Income</List.Item>
                  <List.Item>Uncategorized Income</List.Item>
                </List>
              </Grid.Column>
            </Grid.Row>
            <GridRowColumn className="align-left">
              <Text>
                We ask this to ensure that we account for any income that may
                have mistakenly gone to a personal account.
              </Text>
            </GridRowColumn>
          </Grid>
        </StepHeaders>
      </GridRowColumn>

      <Grid.Row className="buttons-grid">
        <Grid.Column width={6} />
        <Grid.Column width={2}>
          <Button
            onClick={() => {
              setHasBusinessIncomeToAdd(true)
              if (setStepContext) {
                setStepContext((prev) => ({
                  ...prev,
                  hasBusinessIncomeToAdd: true,
                }))
              }
            }}
            variant="selector"
            fullWidth
            active={hasBusinessIncomeToAdd === true}
          >
            Yes
          </Button>
        </Grid.Column>
        <Grid.Column width={2}>
          <Button
            onClick={() => {
              // Show confirmation if there are transactions
              if (addedTransactionIds.length > 0) {
                setShowConfirmNoDialog(true)
              } else {
                // No transactions, proceed directly
                setHasBusinessIncomeToAdd(false)
                if (setStepContext) {
                  setStepContext((prev) => ({
                    ...prev,
                    hasBusinessIncomeToAdd: false,
                    businessIncomeTransactionIds: [],
                  }))
                }
              }
            }}
            variant="selector"
            fullWidth
            active={hasBusinessIncomeToAdd === false}
          >
            No
          </Button>
        </Grid.Column>
      </Grid.Row>
      {hasBusinessIncomeToAdd && (
        <>
          <GridRowColumn>
            <Text textAlign="center">
              Please add transactions that haven’t already been recorded in
              Heard.
            </Text>
          </GridRowColumn>
          <GridRowColumn spacer={6} width={4}>
            <Button
              variant="secondary"
              fullWidth
              onClick={() => {
                setEditingTransactionId(undefined)
                setTranscationModalColor('natural')
                setShowAddTransactionModal(true)
              }}
            >
              <Icon icon={faPlus} style={{ marginRight: 10 }} />
              Add Transaction
            </Button>
          </GridRowColumn>
          <Grid.Row />
          {showAddTransactionModal && (
            <>
              <GridRowColumn spacer={2} width={12}>
                <span ref={scrollRef} />
                <AddTransactionCard
                  onClose={() => setShowAddTransactionModal(false)}
                  onSave={addTransactionToList}
                  transactionId={editingTransactionId}
                  categoryType="Income"
                  categoryFilter={filterCategories}
                  backgroundColor={transcationModalColor}
                />
              </GridRowColumn>
              <Grid.Row />
            </>
          )}

          {addedTransactionIds.length > 0 && <TransactionHeader />}
          <GridRowColumn short className="transaction-grid">
            {addedTransactionIds.map((id) => (
              <TransactionCard
                key={id}
                transactionId={id}
                variant="edit"
                editAction={editTransaction}
                deleteAction={deleteTransaction}
                backgroundColor="stone"
              />
            ))}
          </GridRowColumn>
        </>
      )}

      <Dialog
        open={showConfirmNoDialog}
        onClose={() => setShowConfirmNoDialog(false)}
      >
        <Dialog.Content size="sm">
          <Dialog.Title>
            Please review and delete transactions first
          </Dialog.Title>
          <Dialog.Description className="mt-2">
            You have {addedTransactionIds.length} transaction
            {addedTransactionIds.length !== 1 ? 's' : ''} added. Please delete
            all transactions before selecting &quot;No&quot;. You can delete
            transactions individually using the delete button on each
            transaction card.
          </Dialog.Description>
          <Dialog.Footer className="mt-6">
            <Dialog.Close asChild>
              <UIButton variant="secondary">Got it</UIButton>
            </Dialog.Close>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>
    </Grid>
  )
}
